import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { OidcSecurityService } from 'angular-auth-oidc-client'; //'@core/auth/angular-auth-oidc-client';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { filter, map, take, tap } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { Notification, MenuItem, UserInfo, BannerMessage, MeteringPoint } from '@model';
import { MenuItemType, HelpState, Sidebar } from '@enums';
import * as fromHelpStore from '@appStore/help';
import * as fromAccountStore from '@appStore/account';
import * as fromAccountState from '@appStore/account/reducers';
import * as fromAppStore from '@appStore/app';  
import * as fromInformationStore from '@core/store';
import * as fromMeteringPointStore from '@appStore/metering-points';
import { SubSink } from 'subsink';
import { config } from 'process';

@Component({
    selector: 'app-nav-buttons',
    templateUrl: './nav-buttons.component.html',
    standalone: false
})
export class NavButtonsComponent implements OnInit, OnChanges {
  @Input()
  isHandset: boolean;
  @Input()
  isLoggedIn: boolean;
  @Input()
  unreadNotificationCount: number;
  @Input()
  unreadNotifications: Notification[]
  @Input()
  bannerMessages: BannerMessage[]
  @Input()
  unreadMessagesCount: number;
  @Input()
  userInfo: UserInfo;
  @Input()
  unreadTipsCount: number;
  @Input()
  meteringPoints: { meteringPoints: MeteringPoint[]; loaded: boolean };

  appRouter: Router;
  _unreadNotifications: Notification[] = [];
  helpMenuItems: MenuItem[] = [];
  myMenuItems: MenuItem[] = [];
  notificationMenuItems: MenuItem[] = [];
  userDisplayName = '';
  isTipWindowOpened = false;
  
  private subs = new SubSink();
  
  constructor(private router: Router,
    private helpStore: Store<fromHelpStore.State>,
    private accountStore: Store<fromAccountState.State>,
    private informationStore: Store<fromInformationStore.InformationState>,
    private meteringPointStore: Store<fromMeteringPointStore.State>,
    private appStore: Store<fromAppStore.AppState>,
    private oidcSecurityService: OidcSecurityService,
    private translate: TranslateService) {
      this.appRouter = router;
  }

  get isShowTips(){
    let showTips = false;
    if (!(!!this.meteringPoints) || !this.meteringPoints.loaded)
      return false;

    return this.meteringPoints.meteringPoints && this.meteringPoints.meteringPoints.length > 0;
  }

  ngOnInit(): void {
    this.notificationMenuItems = [];
    this.myMenuItems = [];
    this.helpMenuItems = [];

    this.initNotificationMenuItems();
    this.setUserDisplayName();
    if (
      this.userInfo &&
      this.userInfo.impersonatedAsId &&
      this.userInfo.impersonatedAsId === '00000000-0000-0000-0000-000000000000'
    ) {
      this.initMyMenuMinimizedAccess();
    } else if (this.userInfo) {
      this.initMyMenu();
    }
    this.initHelpMenu();
  }

  ngOnChanges(changes: SimpleChanges) {
    for (const propName in changes) {
      if (changes.hasOwnProperty(propName)) {
        let change = changes[propName];
        switch (propName) {
          case 'userInfo': {
            this.setUserDisplayName();
            if (
              this.userInfo && this.userInfo.impersonatedAsId 
              && this.userInfo.impersonatedAsId === '00000000-0000-0000-0000-000000000000') {
              this.initMyMenuMinimizedAccess();
            } else if (this.userInfo) {
              this.initMyMenu();
            }
            break;
          }
          case 'unreadNotifications':{
            this._unreadNotifications = this.unreadNotifications;
            if (!this._unreadNotifications){
              this._unreadNotifications = [];
            }
            this.notificationMenuItems = [];
            this.myMenuItems = [];
            this.initNotificationMenuItems();
            this.initMyMenu();
          }
        }
      }
    }

    this.initHelpMenu();
  }

  onSystemMessagesClick(){
    //console.log(`nav-buttons\tonSystemMessagesClick`);
    this.informationStore.dispatch(new fromInformationStore.ShowBanners());
  }

  onTipsClick(){
    this.appStore.dispatch(new fromAppStore.TipsOpen);
    this.appStore.dispatch(new fromAppStore.ActivateSidebar(Sidebar.Tips))
  }

  initNotificationMenuItems() {
    this.notificationMenuItems.push({
      callbackFunction: () => {},
      displayText: this.translate.instant('core.header.notifications.title'),
      iconName: '',
      itemType: MenuItemType.Heading,
      linkUrl: '',
      id: 'top_menu_notification_menu_title'
    } as MenuItem);

    if (this._unreadNotifications && this._unreadNotifications.length > 0) {
      for (let i = 0; i < this._unreadNotifications.length && i < 5; i++) {
        this.notificationMenuItems.push({
          callbackFunction: () => {},
          displayText: this._unreadNotifications[i].description,
          iconName: this._unreadNotifications[i].isAlarm ? '#sx-avbrott' : '#sx-notiser',
          itemType: MenuItemType.Blank,
          linkUrl: '',
          id: 'top_menu_notification_menu_not_' + i
        } as MenuItem);
      }
    } else {
      this.notificationMenuItems.push({
        callbackFunction: () => {},
        displayText: this.translate.instant('core.header.notifications.empty'),
        iconName: '',
        itemType: MenuItemType.InfoText,
        linkUrl: '',
        id: 'top_menu_notification_menu_empty'
      } as MenuItem);
    }
  }

  initSystemMessages = () =>{
    //this.informationStore.select(fromInformationStore.getBannerMessages)
  }

  initHelpMenu = () => {
    this.helpMenuItems = [];

    this.helpMenuItems.push({
      callbackFunction: () => {},
      displayText: this.translate.instant('core.header.help'),
      iconName: '',
      itemType: MenuItemType.Heading,
      linkUrl: '',
      id: 'top_menu_help_menu_title'
    } as MenuItem);

    this.helpMenuItems.push({
      callbackFunction: () => {
        this.helpStore.dispatch(new fromHelpStore.HelpSetState(HelpState.contextualInfo));
        this.appStore.dispatch(new fromAppStore.ActivateSidebar(Sidebar.Help));
      },
      displayText: this.translate.instant('core.help.menu.contextualInfo'),
      iconName: '#sx-pa-aktuell-sida',
      itemType: MenuItemType.Function,
      linkUrl: '',
      id: 'top_menu_help_menu_onpage'
    } as MenuItem);

    this.helpMenuItems.push({
      callbackFunction: () => {
        this.helpStore.dispatch(new fromHelpStore.HelpSetState(HelpState.glossary));
        this.appStore.dispatch(new fromAppStore.ActivateSidebar(Sidebar.Help));
      },
      displayText: this.translate.instant('core.help.menu.glossary'),
      iconName: '#sx-ordlista',
      itemType: MenuItemType.Function,
      linkUrl: '',
      id: 'top_menu_help_menu_words'
    } as MenuItem);

    this.helpMenuItems.push({
      callbackFunction: () => {
        this.helpStore.dispatch(new fromHelpStore.HelpSetState(HelpState.introduction));
        this.appStore.dispatch(new fromAppStore.ActivateSidebar(Sidebar.Help));
      },
      displayText: this.translate.instant('core.help.menu.introduction'),
      iconName: '#sx-introduktion',
      itemType: MenuItemType.Function,
      linkUrl: '',
      id: 'top_menu_help_menu_intro'
    } as MenuItem);

    this.helpMenuItems.push({
      callbackFunction: () => {
        this.helpStore.dispatch(new fromHelpStore.HelpSetState(HelpState.support));
        this.appStore.dispatch(new fromAppStore.ActivateSidebar(Sidebar.Help));
      },
      displayText: this.translate.instant('core.help.menu.support'),
      iconName: '#sx-kontakta-oss',
      itemType: MenuItemType.Function,
      linkUrl: '',
      id: 'top_menu_help_menu_contact'
    } as MenuItem);
  };

  setUserDisplayName(){
    if (!this.userInfo || !this.userInfo.name){
      this.userDisplayName = "";
      return;
    }

    this.userDisplayName = this.userInfo.name;
    if (this.userInfo?.impersonatedAsName){
      this.userDisplayName += ` som ${this.userInfo.impersonatedAsName}`
    }
  }

  initMyMenu = () => {
    this.myMenuItems = [];
    this.myMenuItems.push({
      callbackFunction: () => {},
      displayText: this.userDisplayName,
      iconName: '',
      itemType: MenuItemType.Heading,
      linkUrl: '',
      id: 'top_menu_user_menu_title'
    } as MenuItem);

 
    this.myMenuItems.push({
      callbackFunction: this.editInfo,
      displayText: this.translate.instant('core.header.user.profile'),
      iconName: '#sx-energikonto',
      itemType: MenuItemType.Function,
      linkUrl: '',
      id: 'top_menu_user_menu_info'
    } as MenuItem);

    this.myMenuItems.push({
      callbackFunction: this.editNotificationSettings,
      displayText: this.translate.instant('core.header.user.settings'),
      iconName: '#sx-installningar',
      itemType: MenuItemType.Function,
      linkUrl: '',
      id: 'top_menu_user_menu_settings'
    } as MenuItem);
 
    this.myMenuItems.push({
      callbackFunction: this.logout,
      displayText: this.translate.instant('core.header.user.logout'),
      iconName: '#sx-logga-ut',
      itemType: MenuItemType.Function,
      linkUrl: '',
      id: 'top_menu_user_menu_logout'
    } as MenuItem);
  };

  initMyMenuMinimizedAccess = () => {
    this.myMenuItems = [];
    this.myMenuItems.push({
      callbackFunction: this.logout,
      displayText: this.translate.instant('core.header.user.logout'),
      iconName: '#sx-logga-ut',
      itemType: MenuItemType.Function,
      linkUrl: '',
      id: 'top_menu_user_menu_logout'
    } as MenuItem);
  };

  logout = () => {
    this.accountStore.dispatch(new fromAccountStore.UserLoggingOut());
    this.oidcSecurityService.isAuthenticated$
      .pipe(
        filter(authResult => !!authResult && authResult?.allConfigsAuthenticated.some(cfg => cfg.isAuthenticated)),
        map(authResult => authResult.allConfigsAuthenticated.find(cfg => cfg.isAuthenticated)),
        map(configAuthResult => configAuthResult.configId),
        take(1),
      ).subscribe(configId => {
        //console.log(`nav-buttons\tlogout\tclientId: ${configId}`);
        this.oidcSecurityService.logoff(configId).subscribe(result => {
          // we need to wait for the logoff to complete
        });
      });
  };

  
  editInfo = () => {
    this.router.navigate([`/secure/user/info`]);
  };

  inactivateUser = () => {
    this.router.navigate([`/secure/user/inactivate`]);
  };

  editNotificationSettings = () => {
    this.router.navigate([`/secure/user/notificationsettings`]);
  };

  routeToSelfService = () => {
   this.router.navigate([`/secure/user/selfservice`]);
  };

  routeToMessages() {
    this.router.navigate(['/secure/inbox/messages']);
  }
  
  routeToNotifications() {
    this.router.navigate(['/secure/inbox/notifications']);
  }
}
